import React, { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

/**
 * A scrollable container that automatically shows a subtle
 * "Deslize para ver mais" hint whenever its content overflows
 * and the user has not yet reached the bottom.
 *
 * Drop-in usage:
 *   <ScrollableArea className="flex-1 min-h-0" contentClassName="px-6 pt-3">
 *     ...children...
 *   </ScrollableArea>
 */
export default function ScrollableArea({
  className = "",
  contentClassName = "",
  hintLabel = "Deslize para ver mais",
  testid,
  children,
}) {
  const ref = useRef(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [atBottom, setAtBottom] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const check = () => {
      const overflow = el.scrollHeight - el.clientHeight > 4;
      setHasOverflow(overflow);
      setAtBottom(el.scrollTop + el.clientHeight >= el.scrollHeight - 4);
    };

    check();
    el.addEventListener("scroll", check, { passive: true });
    const ro = new ResizeObserver(check);
    ro.observe(el);
    const mo = new MutationObserver(check);
    mo.observe(el, { childList: true, subtree: true, characterData: true });
    return () => {
      el.removeEventListener("scroll", check);
      ro.disconnect();
      mo.disconnect();
    };
  }, []);

  const showHint = hasOverflow && !atBottom;

  return (
    <div className={`relative ${className}`} data-testid={testid}>
      <div
        ref={ref}
        className={`absolute inset-0 overflow-y-auto thin-scroll ${contentClassName}`}
      >
        {children}
      </div>

      {/* Bottom fade cue */}
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-[var(--paper)] via-[var(--paper)]/75 to-transparent transition-opacity duration-500 ${
          showHint ? "opacity-100" : "opacity-0"
        }`}
      />

      {/* Animated hint pill */}
      <div
        data-testid={testid ? `${testid}-hint` : "scroll-hint"}
        aria-hidden="true"
        className={`pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-1.5 flex items-center gap-1.5 px-2.5 py-1 border border-[rgba(212,175,55,0.5)] bg-[rgba(10,10,10,0.85)] backdrop-blur-sm shadow-[0_6px_18px_rgba(0,0,0,0.45)] transition-all duration-500 ${
          showHint ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
        }`}
      >
        <span className="font-body text-[0.52rem] uppercase tracking-[0.28em] text-[var(--gold)] whitespace-nowrap">
          {hintLabel}
        </span>
        <ChevronDown
          size={12}
          strokeWidth={1.8}
          className="text-[var(--gold)] scroll-bounce"
        />
      </div>
    </div>
  );
}
